import React, { useEffect, useRef } from 'react';
import styles from './MoonshotCreateTeamForm.less';
import Select, { components } from 'react-select';
import _ from 'underscore';
import { useMoonshotCreateTeamQuery } from '@trello/graphql/generated';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Team } from 'app/scripts/debug/constants';
import { forNamespace } from 'app/common/lib/util/i18n';

const format = forNamespace(['moonshot', 'create team']);
const teamTypesFormat = forNamespace(['moonshot', 'team types']);

interface CreateTeamProps {
  onChangeCategory: (opt: Option) => void;
  onChangeTeamName: React.ChangeEventHandler<HTMLInputElement>;
  teamName: string;
  teamNameError: string;
  categoryError: string;
}

interface CreateTeamFormComponentProps {
  readonly isFocused: boolean;
  readonly isSelected: boolean;
}

interface Option {
  value: string;
  label: string;
}

const items = _.shuffle<Option>([
  { value: 'it', label: teamTypesFormat('information technology') },
  { value: 'operations', label: teamTypesFormat('operations') },
  { value: 'sales', label: teamTypesFormat('sales') },
  { value: 'marketing', label: teamTypesFormat('marketing') },
  { value: 'human resources', label: teamTypesFormat('human resources') },
  { value: 'engineering', label: teamTypesFormat('engineering') },
  { value: 'product', label: teamTypesFormat('product') },
  { value: 'design', label: teamTypesFormat('design') },
  { value: 'support', label: teamTypesFormat('support') },
]).concat({ value: 'other', label: teamTypesFormat('other') });

const Input: React.FC<CreateTeamFormComponentProps> = ({ ...props }) => (
  <components.Input {...props} className={styles.createTeamMoonshotDropdown} />
);

export const MoonshotCreateTeamForm: React.FC<CreateTeamProps> = ({
  categoryError,
  teamName,
  teamNameError,
  onChangeCategory,
  onChangeTeamName,
}) => {
  const teamInputRef = useRef<HTMLInputElement>(null);
  const { data } = useMoonshotCreateTeamQuery({
    variables: {
      memberId: 'me',
    },
  });

  // Urgh, we want to auto focus the first input but the team picker from
  // Coffeescript land steals the focus, so we need to wait a little
  useEffect(() => {
    setTimeout(() => teamInputRef.current?.focus(), 100);
  }, [teamInputRef]);

  return (
    <ErrorBoundary tags={{ ownershipArea: Team.BizTeam }}>
      <div className={styles.moonshotCreateTeamFormContent}>
        <div className={styles.moonshotCreateTeamFormHeadline}>
          <h1>
            <span role="img" aria-label="waving hand">
              👋
            </span>{' '}
            {format('create team heading', {
              username: (
                <strong key="username">{data?.member?.fullName || ''}</strong>
              ),
            })}
          </h1>
        </div>
        <div className={styles.moonshotCreateTeamForm}>
          <label
            className={styles.moonshotCreateTeamLabel}
            htmlFor="moonshotCreateTeam"
          >
            {format('team name input label')}
            <span className={styles.createTeamLabelstar}>*</span>
          </label>
          <input
            id="moonshotCreateTeam"
            className={styles.moonshotCreateTeamInput}
            type="text"
            autoComplete="off"
            spellCheck={false}
            onChange={onChangeTeamName}
            autoFocus={true}
            placeholder={format('team name input placeholder')}
            value={teamName}
            maxLength={100}
            minLength={1}
            aria-describedby="moonshotCreateTeamNameError"
            ref={teamInputRef}
          />
          <div
            id="moonshotCreateTeamNameError"
            role="alert"
            aria-atomic
            className={styles.error}
          >
            {!!teamNameError && <p>{teamNameError}</p>}
          </div>
          <label
            className={styles.moonshotCreateTeamLabel}
            htmlFor="moonshotCreateTeamCategory"
          >
            {format('team type select label')}
            <span className={styles.createTeamLabelstar}>*</span>
          </label>
          <Select
            blurInputOnSelect={true}
            className={styles.moonshotCreateTeamDropdown}
            placeholder={format('team type select placeholder')}
            options={items}
            inputId="moonshotCreateTeamCategory"
            aria-describedby="moonshotCreateTeamCategoryError"
            onChange={onChangeCategory}
            components={{ Input, IndicatorSeparator: () => null }}
          />
          <div
            id="moonshotCreateTeamCategoryError"
            role="alert"
            aria-atomic
            className={styles.error}
          >
            {!!categoryError && <p>{categoryError}</p>}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
